import { computed, readonly, ref, shallowRef } from "vue";
import { authorizeZeroAmountPayment } from "@/core/api/graphql/payment/mutations/authorizeZeroAmountPayment";
import { deleteSavedCreditCard } from "@/core/api/graphql/payment/mutations/deletePayment";
import { setDefaultCreditCard } from "@/core/api/graphql/payment/mutations/setDefaultPayment";
import { updateSavedCreditCard } from "@/core/api/graphql/payment/mutations/updatePayment";
import { getCustomerPaymentMethods } from "@/core/api/graphql/payment/queries/getCustomerPaymentMethods";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import { currencyConfig } from "@/shared/checkout/currency-settings";
import { getNonDefaultPaymentInstrumentsWithSameDataAreaId } from "../utils/paymentUtils";
import { useUser } from "./useUser";
import type {
  AuthorizePaymentResultType,
  InputAddressTypeExtension,
  InputKeyValueType,
  KeyValueType,
  PaymentInstrumentDetailsType,
} from "@/core/api/graphql/types";

const loading = ref(false);
const payments = shallowRef<PaymentInstrumentDetailsType[] | undefined>([]);
const authorizationParameters = shallowRef<KeyValueType[]>([]);

export function useUserPayments() {
  const { user } = useUser();
  const { currencyCode } = globals;

  function setAuthorizationParameters(parameters: KeyValueType[]) {
    authorizationParameters.value = parameters;
  }

  function getBillingDataAreaId(currencyCodeParam: string): string {
    const billingDataAreaId = currencyConfig[currencyCodeParam]?.billingDataAreaId;
    if (!billingDataAreaId) {
      throw new Error(`Unsupported currency code: ${currencyCodeParam}`);
    }
    return billingDataAreaId;
  }

  async function fetchPayments() {
    loading.value = true;

    try {
      const billingDataAreaId = getBillingDataAreaId(currencyCode);
      payments.value = (await getCustomerPaymentMethods(user.value?.id ?? "", billingDataAreaId)).paymentInstruments;
    } catch (e) {
      Logger.error(`${useUserPayments.name}.${fetchPayments.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function addPayment(
    parameters: InputKeyValueType[],
    billingAddress: InputAddressTypeExtension,
    captchaCode: string,
  ): Promise<AuthorizePaymentResultType> {
    loading.value = true;

    try {
      const result = await authorizeZeroAmountPayment(user.value?.id ?? "", {
        billingAddress,
        parameters,
        captchaCode,
      });

      if (result.isSuccess) {
        // Assign fresh payments
        payments.value = result.paymentInstruments;
      }

      // Return object as authorization result
      return {
        isSuccess: result.isSuccess,
        errorMessage: (result.errorCode ?? result.errorMessage ?? "") as string,
      } as AuthorizePaymentResultType;
    } catch (e) {
      Logger.error(`${useUserPayments.name}.${addPayment.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function setDefaultPaymentInstrument(paymentInstrumentTokenId: string, dataAreaId: string): Promise<void> {
    loading.value = true;
    const payload = {
      paymentInstrumentTokenId,
      customerId: user.value?.id ?? "",
      dataAreaId,
      storeId: globals.storeId,
    };
    try {
      const result = await setDefaultCreditCard(payload);

      // Validate proper response to ensure setDefaultCreditCard was successful
      if (!result.isSuccess) {
        Logger.error(`${useUserPayments.name}.${setDefaultPaymentInstrument.name}`, result.errorMessage);
        return;
      }

      // Assign fresh payments
      payments.value = result.paymentInstruments;
    } catch (e) {
      Logger.error(`${useUserPayments.name}.${setDefaultPaymentInstrument.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function updatePaymentInstrument(
    paymentInstrumentTokenId: string,
    paymentInstrumentOriginalDataAreaId: string,
    parameters: InputKeyValueType[],
    billingAddress: InputAddressTypeExtension,
    isDefault = false,
    captchaCode: string,
  ): Promise<AuthorizePaymentResultType> {
    loading.value = true;
    let newDefaultPaymentInstrumentId = "";
    if (isDefault) {
      // update = delete+create;if the updated card is default, we need to set a new default card before it is deleted. Otherwise, the update will fail. A default card can be deleted if it is the only card.
      const nonDefaultPaymentInstrumentsWithSameDataAreaId = getNonDefaultPaymentInstrumentsWithSameDataAreaId(
        payments.value,
        paymentInstrumentTokenId,
        paymentInstrumentOriginalDataAreaId,
      );
      newDefaultPaymentInstrumentId =
        nonDefaultPaymentInstrumentsWithSameDataAreaId?.at(0)?.paymentInstrumentTokenId ?? "";
    }
    const payload = {
      cardDetails: {
        billingAddress,
        parameters,
        captchaCode,
        memberId: user.value?.id ?? "",
      },
      paymentInstrumentId: paymentInstrumentTokenId,
      paymentInstrumentOriginalDataAreaId,
      newDefaultPaymentInstrumentId,
    };
    try {
      const result = await updateSavedCreditCard(payload);

      // Validate proper response to ensure updateSavedCreditCard was successful
      if (!result.isSuccess) {
        Logger.error(`${useUserPayments.name}.${updatePaymentInstrument.name}`, result.errorMessage);
        return { isSuccess: result.isSuccess, errorMessage: result.errorMessage } as AuthorizePaymentResultType;
      }

      // Assign fresh payments
      payments.value = result.paymentInstruments;

      // Return object as authorization result
      return {
        isSuccess: result.isSuccess,
        errorMessage: result.errorMessage,
      } as AuthorizePaymentResultType;
    } catch (e) {
      Logger.error(`${useUserPayments.name}.${updatePaymentInstrument.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function deletePaymentInstrument(
    paymentInstrumentTokenId: string,
    dataAreaId: string,
    isDefault = false,
  ): Promise<void> {
    loading.value = true;
    let newDefaultPaymentInstrumentId = "";
    if (isDefault) {
      // if the deleted card is default, we need to set a new default card before it is deleted. Otherwise, the deletion will fail. A default card can be deleted if it is the only card.
      const nonDefaultPaymentInstrumentsWithSameDataAreaId = getNonDefaultPaymentInstrumentsWithSameDataAreaId(
        payments.value,
        paymentInstrumentTokenId,
        dataAreaId,
      );
      newDefaultPaymentInstrumentId =
        nonDefaultPaymentInstrumentsWithSameDataAreaId?.at(0)?.paymentInstrumentTokenId ?? "";
    }
    const payload = {
      paymentInstrumentId: paymentInstrumentTokenId,
      newDefaultPaymentInstrumentId,
      customerId: user.value?.id ?? "",
      dataAreaId: dataAreaId,
      userId: "",
      storeId: globals.storeId,
    };
    try {
      const result = await deleteSavedCreditCard(payload);

      // Validate proper response to ensure deleteSavedCreditCard was successful
      if (!result.isSuccess) {
        Logger.error(`${useUserPayments.name}.${deletePaymentInstrument.name}`, result.errorMessage);
        return;
      }
      payments.value = result.paymentInstruments;
    } catch (e) {
      Logger.error(`${useUserPayments.name}.${deletePaymentInstrument.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    fetchPayments,
    addPayment,
    setDefaultPaymentInstrument,
    setAuthorizationParameters,
    deletePaymentInstrument,
    updatePaymentInstrument,
    loading: readonly(loading),
    payments: computed(() => payments.value),
    authorizationParameters: computed(() => authorizationParameters.value),
  };
}
